import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Map, { Marker, Popup } from 'react-map-gl/maplibre';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import PropertyCard from '../components/PropertyCard';
import SearchFilters from '../components/SearchFilters';
import { FiMapPin } from 'react-icons/fi';
import LocationAutocomplete from '../components/LocationAutocomplete';
import 'maplibre-gl/dist/maplibre-gl.css';
import './MapSearch.css';

const MapSearch = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const mapRef = useRef(null);

  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [hoveredProperty, setHoveredProperty] = useState(null);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Default viewport (Uberlândia, MG)
  const [viewState, setViewState] = useState({
    longitude: -48.2766,
    latitude: -18.9188,
    zoom: 12
  });

  // Read URL parameters on mount and set initial location
  useEffect(() => {
    const place_id = searchParams.get('place_id');
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const query = searchParams.get('q');

    // Set search query from URL if exists
    if (query) {
      setSearchQuery(query);
    }

    // If coordinates are provided directly, use them
    if (lat && lng) {
      setViewState({
        longitude: parseFloat(lng),
        latitude: parseFloat(lat),
        zoom: 13
      });
    }
    // If only place_id is provided, fetch coordinates
    else if (place_id) {
      const fetchPlaceDetails = async () => {
        try {
          const response = await api.get('/api/properties/place-details', {
            params: { place_id }
          });

          if (response.data && response.data.geometry) {
            const { lat, lng } = response.data.geometry.location;
            setViewState({
              longitude: lng,
              latitude: lat,
              zoom: 13
            });
          }
        } catch (error) {
          console.error('Error fetching place details from URL:', error);
        }
      };

      fetchPlaceDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Fetch properties when map moves
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchPropertiesInBounds();
    }, 500); // Debounce map movements

    return () => clearTimeout(timeoutId);
  }, [viewState, filters]);

  const fetchPropertiesInBounds = async () => {
    if (!mapRef.current) return;

    setLoading(true);
    try {
      const map = mapRef.current.getMap();
      const bounds = map.getBounds();

      const params = new URLSearchParams({
        sw_lat: bounds.getSouth(),
        sw_lng: bounds.getWest(),
        ne_lat: bounds.getNorth(),
        ne_lng: bounds.getEast(),
        per_page: 100
      });

      // Add filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await api.get(`/api/properties/bounds?${params.toString()}`);
      const props = response.data.properties || [];
      console.log('Properties loaded:', props.length, 'Sample:', props.slice(0, 2));
      setProperties(props);
    } catch (error) {
      console.error('Error fetching properties:', error);
      // If bounds endpoint doesn't exist, use nearby endpoint with center
      try {
        const map = mapRef.current.getMap();
        const center = map.getCenter();
        const zoom = map.getZoom();

        // Calculate approximate radius based on zoom level
        const radius = Math.max(5, Math.min(50, 100 / Math.pow(2, zoom - 10)));

        const params = new URLSearchParams({
          lat: center.lat,
          lng: center.lng,
          radius: radius,
          per_page: 100
        });

        Object.entries(filters).forEach(([key, value]) => {
          if (value) params.append(key, value);
        });

        const response = await api.get(`/api/properties/nearby?${params.toString()}`);
        setProperties(response.data.properties || []);
      } catch (fallbackError) {
        console.error('Error with fallback fetch:', fallbackError);
        toast.error('Erro ao carregar propriedades');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = async (location) => {
    if (!location || !location.placeId) return;

    setSelectedLocation(location);
    setSearchQuery(location.mainText);

    try {
      // Get place details to get coordinates
      const response = await api.get('/api/properties/place-details', {
        params: { place_id: location.placeId }
      });

      if (response.data && response.data.geometry) {
        const { lat, lng } = response.data.geometry.location;
        setViewState({
          longitude: lng,
          latitude: lat,
          zoom: 13
        });
      }
    } catch (error) {
      console.error('Error getting place details:', error);
      toast.error('Erro ao buscar localização');
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleMarkerClick = (property) => {
    setSelectedProperty(property);
    setViewState({
      ...viewState,
      longitude: property.longitude,
      latitude: property.latitude,
      zoom: Math.max(viewState.zoom, 14)
    });
  };

  return (
    <div className="map-search-page">
      {/* Search Bar */}
      <div className="map-search-header">
        <div className="map-search-bar">
          <LocationAutocomplete
            value={searchQuery}
            onChange={handleLocationChange}
            placeholder={t('mapSearch.searchPlaceholder')}
            className="w-100"
          />
        </div>

        <div className="map-filters">
          <SearchFilters onFilterChange={handleFilterChange} initialFilters={filters} compact />
        </div>
      </div>

      {/* Map Container */}
      <div className="map-container">
        <Map
          ref={mapRef}
          {...viewState}
          onMove={evt => setViewState(evt.viewState)}
          mapStyle="https://basemaps.cartocdn.com/gl/positron-gl-style/style.json"
          style={{ width: '100%', height: '100%' }}
          attributionControl={false}
        >
          {/* Property Markers */}
          {properties.map((property) => (
            <Marker
              key={property.id}
              longitude={property.longitude}
              latitude={property.latitude}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                handleMarkerClick(property);
              }}
            >
              <div
                className={`map-marker ${hoveredProperty?.id === property.id ? 'hovered' : ''} ${selectedProperty?.id === property.id ? 'selected' : ''}`}
                onMouseEnter={() => setHoveredProperty(property)}
                onMouseLeave={() => setHoveredProperty(null)}
              >
                <FiMapPin className="marker-icon" />
                <span className="marker-price">
                  R$ {property.price_per_night}
                </span>
              </div>
            </Marker>
          ))}

          {/* Selected Property Popup */}
          {selectedProperty && (
            <Popup
              longitude={selectedProperty.longitude}
              latitude={selectedProperty.latitude}
              anchor="bottom"
              onClose={() => setSelectedProperty(null)}
              closeButton={true}
              closeOnClick={false}
              maxWidth="320px"
            >
              <div className="map-popup">
                <PropertyCard
                  property={selectedProperty}
                  compact
                />
              </div>
            </Popup>
          )}
        </Map>

        {/* Loading Indicator */}
        {loading && (
          <div className="map-loading">
            <div className="spinner-small"></div>
            <span>Buscando propriedades...</span>
          </div>
        )}

        {/* Results Count */}
        <div className="map-results-count">
          {properties.length} propriedades encontradas
        </div>
      </div>

      {/* Property List Sidebar (Optional - can be toggled) */}
      <div className="map-sidebar">
        <div className="sidebar-header">
          <h3>Propriedades no Mapa</h3>
          <p className="text-muted small">
            Mova o mapa para buscar em diferentes áreas
          </p>
        </div>

        <div className="property-list-scroll">
          {properties.length === 0 ? (
            <div className="no-results text-center py-4">
              <p>Nenhuma propriedade encontrada nesta área</p>
              <p className="text-muted small">Tente mover ou dar zoom no mapa</p>
            </div>
          ) : (
            properties.map((property) => (
              <div
                key={property.id}
                className={`sidebar-property-card ${hoveredProperty?.id === property.id ? 'hovered' : ''}`}
                onMouseEnter={() => setHoveredProperty(property)}
                onMouseLeave={() => setHoveredProperty(null)}
                onClick={() => handleMarkerClick(property)}
              >
                <PropertyCard
                  property={property}
                  compact
                />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MapSearch;
